/**
 * Apple Pay initialization and utilities
 * Adapted from uniformadvantage initApplePay.js for React
 * Uses PayPal's Apple Pay SDK (same as uniformadvantage.com)
 * 
 * Usage:
 *   import initApplePay, { mapApplePayContactToAddress, createApplePaySession, loadApplePaySdk } from '../utils/initApplePay';
 *   const config = await initApplePay();
 *   if (config) {
 *       // Apple Pay is available
 *   }
 */

// localhost-only query-param mock mode
export const APPLEPAY_DEV_MOCK_MODE = false;

/**
 * Read mock params from URL query string (localhost only)
 * @returns {{ enabled: boolean, scenario: string }}
 */
export const getApplePayMockParams = () => {
    try {
        const hostname = window?.location?.hostname;
        const isLocalhost = hostname === 'localhost';
        if (!isLocalhost) return { enabled: false, scenario: 'success' };

        const params = new URLSearchParams(window?.location?.search || '');
        const enabled = params.get('applepayMock') === '1';
        const scenario = (params.get('applepayScenario') || 'success').trim();
        return { enabled, scenario };
    } catch (e) {
        return { enabled: false, scenario: 'success' };
    }
};

/**
 * Map an Apple Pay contact object to a normalized address
 * @param {Object} contact - Apple Pay contact (shipping or billing)
 * @returns {Object|null} Normalized address object
 */
export const mapApplePayContactToAddress = (contact) => {
    if (!contact) return null;

    const fullName =
        contact?.name?.fullName
        || [contact?.givenName, contact?.familyName].filter(Boolean).join(' ')
        || '';
    const firstName =
        contact?.name?.givenName
        || contact?.givenName
        || (fullName ? fullName.split(' ')[0] : '');
    const lastName =
        contact?.name?.familyName
        || contact?.familyName
        || (fullName ? fullName.split(' ').slice(1).join(' ') : '');

    const addr1 = contact?.addressLines?.[0] || '';
    const addr2 = contact?.addressLines?.[1] || '';

    return {
        firstName,
        lastName,
        fullName,
        address1: addr1,
        address2: addr2,
        city: contact?.locality || '',
        stateCode: contact?.administrativeArea || '',
        postalCode: contact?.postalCode || '',
        countryCode: contact?.countryCode || 'US',
        phone: contact?.phoneNumber || '',
        email: contact?.emailAddress || '',
    };
};

/**
 * Dynamically load the Apple Pay SDK script if not already present
 * @param {string} [sdkUrl] - Optional custom SDK URL
 * @returns {Promise<boolean>} true when loaded
 */
export const loadApplePaySdk = (sdkUrl) => {
    return new Promise((resolve) => {
        if (document.querySelector('script[src*="applepay.cdn-apple.com"]')) {
            resolve(!!window.ApplePaySession);
            return;
        }

        const script = document.createElement('script');
        script.src = sdkUrl || 'https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js';
        script.async = true;
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.head.appendChild(script);
    });
};

/**
 * Build an ApplePayPaymentRequest from config and amount
 * @param {Object} config - Apple Pay config from initApplePay()
 * @param {number} amount - Payment amount
 * @returns {Object} ApplePayPaymentRequest
 */
export const buildPaymentRequest = (config, amount) => {
    return {
        countryCode: config.countryCode || 'US',
        currencyCode: 'USD',
        merchantCapabilities: config.merchantCapabilities || ['supports3DS'],
        supportedNetworks: config.supportedNetworks || ['visa', 'masterCard', 'amex', 'discover'],
        requiredBillingContactFields: ['postalAddress', 'name', 'phone', 'email'],
        requiredShippingContactFields: ['postalAddress', 'name', 'phone', 'email'],
        total: {
            label: 'Total',
            amount: amount.toFixed(2),
            type: 'final',
        },
    };
};


/**
 * Mock Apple Pay contacts for dev/test flows
 */
export const APPLEPAY_MOCK_CONTACTS = {
    shipping: {
        givenName: 'Debug',
        familyName: 'ApplePay',
        addressLines: ['30 Rockefeller PlazaSHIP', ''],
        locality: 'Fort Lauderale',
        administrativeArea: 'FL',
        postalCode: '33301',
        countryCode: 'US',
        emailAddress: 'debug@test.com',
        phoneNumber: '2125551234',
    },
    billing: {
        givenName: 'Debug',
        familyName: 'ApplePay',
        addressLines: ['30 Rockefeller PlazaBILL', ''],
        locality: 'Fort Lauderale',
        administrativeArea: 'FL',
        postalCode: '33301',
        countryCode: 'US',
        emailAddress: 'debug@test.com',
        phoneNumber: '2125551234',
    },
};

/**
 * Create and start an Apple Pay session (mirrors uniformadvantage pattern)
 * @param {Object} options
 * @param {Object} options.config - Apple Pay config from initApplePay()
 * @param {number} options.amount - Payment amount
 * @param {Function} options.onPaymentAuthorized - Called with (event.payment, applepay, session), must resolve/reject
 * @param {Function} [options.onCancel] - Called when user cancels
 * @param {Function} [options.onError] - Called on session-level errors
 * @returns {ApplePaySession} The started session
 */
export const createApplePaySession = ({ config, amount, onPaymentAuthorized, onShippingContactSelected, onCancel, onError }) => {
    const applepay = window.paypal.Applepay();
    const paymentRequest = buildPaymentRequest(config, amount);
    const session = new window.ApplePaySession(4, paymentRequest);

    session.onvalidatemerchant = async (event) => {
        try {
            const validationResult = await applepay.validateMerchant({
                validationUrl: event.validationURL,
                displayName: 'UA Corporate',
            });
            session.completeMerchantValidation(validationResult.merchantSession);
        } catch (err) {
            console.error('[ApplePay] Merchant validation failed:', err);
            if (onError) onError('Apple Pay merchant validation failed. Please try again.');
            session.abort();
        }
    };

    // Recalculate tax/totals when user changes shipping address on the Apple Pay sheet
    if (onShippingContactSelected) {
        session.onshippingcontactselected = async (event) => {
            try {
                const result = await onShippingContactSelected(event.shippingContact);
                session.completeShippingContactSelection({
                    newTotal: {
                        label: 'Total',
                        type: 'final',
                        amount: result.newTotal,
                    },
                });
            } catch (err) {
                console.error('[ApplePay] onshippingcontactselected error:', err);
                // On error, keep the current total so the sheet doesn't break
                session.completeShippingContactSelection({
                    newTotal: {
                        label: 'Total',
                        type: 'final',
                        amount: amount.toFixed(2),
                    },
                });
            }
        };
    }

    session.onpaymentauthorized = async (event) => {
        try {
            await onPaymentAuthorized(event.payment, applepay, session);
            session.completePayment(window.ApplePaySession.STATUS_SUCCESS);
        } catch (err) {
            console.error('[ApplePay] onpaymentauthorized error:', err);
            try {
                session.completePayment(window.ApplePaySession.STATUS_FAILURE);
            } catch (e) {
                console.warn('[ApplePay] Failed to complete session with failure status', e);
            }
            if (onError) onError('Apple Pay payment failed. Please try again or use another payment method.');
        }
    };

    session.oncancel = () => {
        // console.log('[ApplePay] Session cancelled');
        if (onCancel) onCancel();
    };

    session.begin();
    return session;
};

/**
 * Initialize Apple Pay - main entry point (same as uniformadvantage)
 * Checks all requirements and returns config if eligible
 * @returns {Promise<Object|null>} Apple Pay config or null if not available
 */
const initApplePay = async () => {
    // console.log('[ApplePay] Checking browser support...');
    if (!window.ApplePaySession) {
        // console.log('[ApplePay] Not available: Missing ApplePaySession');
        return null;
    }

    // console.log('[ApplePay] Checking device capability...');
    if (!window.ApplePaySession.canMakePayments()) {
        // console.log('[ApplePay] Not available: Device cannot make payments');
        return null;
    }

    // console.log('[ApplePay] Checking PayPal SDK...');
    if (!window.paypal || !window.paypal.Applepay) {
        // console.log('[ApplePay] Not available: PayPal Apple Pay SDK not loaded');
        return null;
    }

    try {
        // console.log('[ApplePay] Getting merchant config...');
        const applepay = window.paypal.Applepay();
        const config = await applepay.config();

        if (!config || !config.isEligible) {
            // console.log('[ApplePay] Not available: Merchant not eligible');
            return null;
        }
        
        // console.log('[ApplePay] Ready to use');

        return config;
    } catch (err) {
        console.error('[ApplePay] Error:', err.message);
        return null;
    }
};

export default initApplePay;
